import useDebounce from 'shared/composables/debounce';
import useGridPageConfig from '@/composables/grid-page/grid-page-config';
import useGridPageRepo from '@/composables/grid-page/grid-page-repo';
import { gridPageType } from 'shared/constants.json';
import { ref } from 'vue';

const textCardMinHeights = ref({});
const userAdjustedRowHeights = ref({});

export default function useGridPageTextCardHeight() {
  const gridPageRepo = useGridPageRepo();
  const gridPageConfig = useGridPageConfig();
  const debounceSvc = useDebounce();

  const calculateTextCardHeight = (contentElement) => {
    if (!contentElement) {
      return null;
    }

    const rect = contentElement.getBoundingClientRect();
    return Math.ceil(rect.height + 20);
  };

  const getMinHeightForRow = (row) => {
    const rowTiles = gridPageRepo.selectMultipleGridPageTiles(row.tiles.map(({ uid }) => uid));

    const textCardTiles = rowTiles.filter((tile) => tile.type === gridPageType.text);
    if (textCardTiles.length === 0) {
      return gridPageConfig.getMinHeight(rowTiles);
    }

    const textCardHeights = textCardTiles
      .map((tile) => textCardMinHeights.value[tile.uid])
      .filter((height) => height !== undefined && height !== null);

    if (textCardHeights.length === 0) {
      return gridPageConfig.getMinHeight(rowTiles);
    }

    const maxTextCardHeight = Math.max(...textCardHeights);
    const configMinHeight = gridPageConfig.getMinHeight(rowTiles);

    return Math.max(maxTextCardHeight, configMinHeight);
  };

  const adjustRowHeightForTextCard = (gridPageTile, contentElement) => {
    const actualTile = gridPageRepo.gridPageTiles.value.find((t) => t.uid === gridPageTile.uid);
    if (!actualTile) {
      return;
    }

    const actualRow = gridPageRepo.gridPageRows.value.find((r) => r.uid === actualTile.gridPageRow.uid);
    if (!actualRow) {
      return;
    }

    if (userAdjustedRowHeights.value[actualRow.uid] === undefined) {
      userAdjustedRowHeights.value[actualRow.uid] = actualRow.height;
    }

    const textCardHeight = calculateTextCardHeight(contentElement.$el);
    if (textCardHeight === null) {
      return;
    }

    textCardMinHeights.value[gridPageTile.uid] = textCardHeight;

    const requiredHeightFromContent = getMinHeightForRow(actualRow);
    const originalHeight = userAdjustedRowHeights.value[actualRow.uid];

    const requiredHeight = Math.max(requiredHeightFromContent, originalHeight);

    if (requiredHeight !== actualRow.height) {
      const updateFn = () => {
        gridPageRepo.updateGridPageRow({ uid: actualRow.uid, height: requiredHeight }, { commitToRemote: false });
      };
      debounceSvc.debounce(updateFn, 300);
    }
  };

  const setUserAdjustedHeight = (height, rowUid) => {
    userAdjustedRowHeights.value[rowUid] = height;
  };

  return {
    adjustRowHeightForTextCard,
    setUserAdjustedHeight,
    getMinHeightForRow,
    textCardMinHeights,
  };
}
